import React, { useEffect, useState } from 'react'
import startCase from 'lodash/startCase'
import get from 'lodash/get'
import moment from 'moment'
import { useMutation } from 'urql'

import { Button, Heading, HStack, Select, Text, Tooltip } from '@chakra-ui/react'
import { RentalApplication } from '@homevest/types/rental-applications'
import { AGREEMENT_STATUSES } from '@homevest/utils/agreements'
import { CAPABILITY_TYPES } from '@homevest/utils/capabilities'
import { RENTAL_APPLICATION_STATUSES } from '@homevest/utils/rental-applications'
import { REGIONS } from '@homevest/utils/regions'
import { LEASE_VARIANTS, RENTAL_PROGRAM_TYPES } from '@homevest/utils/rentals'

import {
  SetRentalApplicationPriorityDocument,
  SetRentalApplicationRecievedProductPitchDocument,
  useRentReadyDatesByOpportunityIdQuery,
  SetRentalApplicationRentDocument,
  SetRentalApplicationAutoRemindersDisabledAtDocument
} from 'graphql/generated'

import CrmField from 'components/CrmField'
import { hasCapability } from 'lib/admin-perms'
import { formatMoney } from 'lib/numbers'
import axios from 'lib/axios'
import { Checkbox } from 'components/Toolkit'
import { CopyIcon } from 'components/TailwindUIToolkit/Utils'
import PaymentSchedule from './PaymentSchedule'
import GuarantorBondIdModal from './guarantor-bond-id-modal'
import {
  BuildiumLeaseSyncAlert,
  BuildiumLeaseValue,
  getBuildiumLeaseAlertState,
  useBuildiumLeaseSync
} from './BuildiumLeaseSync'

import { ContentSectionCard } from 'components/TailwindUIToolkit'

const FIELD_TYPES = {
  VALUE: 'value',
  RANGE: 'range',
  SELECT: 'select',
  MULTI_SELECT: 'multi_select'
}

const FIELD_VALUE_TYPES = {
  STRING: 'string',
  INTEGER: 'integer',
  FLOAT: 'float',
  BOOLEAN: 'boolean',
  DATE: 'date',
  MONEY: 'money'
}
const MONTHLY_OPTION_PREMIUM = 50
const POST_LEASE_CREATION_STATUSES = new Set<string>([
  RENTAL_APPLICATION_STATUSES.PENDING_CUSTOMER_RENTAL_AGREEMENT_COMPLETION,
  RENTAL_APPLICATION_STATUSES.PENDING_CUSTOMER_PAYMENT,
  RENTAL_APPLICATION_STATUSES.PENDING_UP_AND_UP_RENTAL_AGREEMENT_COMPLETION,
  RENTAL_APPLICATION_STATUSES.PENDING_PROPERTY_PURCHASE,
  RENTAL_APPLICATION_STATUSES.COMPLETE
])

function ApplicationDetails({
  rentalApplication,
  setRentalApplication,
  admin
}: {
  rentalApplication: RentalApplication & { listedRent?: number | null }
  setRentalApplication: (rentalApplication: RentalApplication) => void
  admin: any
}) {
  const [isSaving, setIsSaving] = useState(false)
  const [, setPriority] = useMutation(SetRentalApplicationPriorityDocument)
  const [, setRent] = useMutation(SetRentalApplicationRentDocument)
  const [, setReceivedInitialProductPitch] = useMutation(
    SetRentalApplicationRecievedProductPitchDocument
  )
  const [, setAutoRemindersDisabledAt] = useMutation(
    SetRentalApplicationAutoRemindersDisabledAtDocument
  )
  const [autoRemindersDisabledFlag, setAutoRemindersDisabledFlag] = useState(
    !!rentalApplication.auto_reminders_disabled_at
  )
  const [guarantorBondId, setGuarantorBondId] = useState<string | null>(
    rentalApplication.guarantor_bond_id
  )
  const [rentalProgramTypeAppliedFor, setRentalProgramTypeAppliedFor] = useState(
    rentalApplication.rental_program_type_applied_for
  )
  const [leaseVariant, setLeaseVariant] = useState(
    rentalApplication.lease_variant || LEASE_VARIANTS.STANDARD
  )
  const [isGuarantorBondIdModalOpen, setIsGuarantorBondIdModalOpen] = useState(false)
  const [{ data, error, fetching: loading }] = useRentReadyDatesByOpportunityIdQuery({
    variables: { opportunityId: rentalApplication.mls_listing_id }
  })

  useEffect(() => {
    setGuarantorBondId(rentalApplication.guarantor_bond_id)
  }, [rentalApplication.guarantor_bond_id])

  useEffect(() => {
    setRentalProgramTypeAppliedFor(rentalApplication.rental_program_type_applied_for)
  }, [rentalApplication.rental_program_type_applied_for])

  useEffect(() => {
    setLeaseVariant(rentalApplication.lease_variant || LEASE_VARIANTS.STANDARD)
  }, [rentalApplication.lease_variant])

  const updateProjectedOccupancyDate = async (occupancyDate: Date | string) => {
    if (isSaving || hasLockedAgreement) {
      return
    }

    setIsSaving(true)

    try {
      const { data } = await axios.post(`/admin/rental_applications/${rentalApplication.id}`, {
        projected_occupancy_date: occupancyDate
      })
      const updatedRentalApplication = {
        ...rentalApplication,
        projected_occupancy_date: data.projected_occupancy_date
      }
      setRentalApplication(updatedRentalApplication)
    } catch (err: any) {
      // eslint-disable-next-line no-undef
      window.alert(err.message)
    }

    setIsSaving(false)
  }

  const updateAutoRemindersDisabledFlag = async () => {
    const newValues = {
      auto_reminders_disabled_at: autoRemindersDisabledFlag ? null : new Date(),
      auto_reminders_disabled_by_admin_id: autoRemindersDisabledFlag ? null : admin.id
    }

    const result = await setAutoRemindersDisabledAt({
      pk_columns: { id: rentalApplication.id },
      ...newValues
    })

    if (result.error) {
      alert(
        'Issue with editing application auto-reminders preference, refresh page and/or contact Eng'
      )
    }

    const updatedValues = result.data.update_rental_applications_by_pk
    setRentalApplication({
      ...rentalApplication,
      auto_reminders_disabled_at: updatedValues.auto_reminders_disabled_at,
      auto_reminders_disabled_by_admin_id: updatedValues.auto_reminders_disabled_by_admin_id
    })
    setAutoRemindersDisabledFlag(!!updatedValues.auto_reminders_disabled_at)
  }

  const updateGuarantorBondId = async (newBondId: string | null): Promise<boolean> => {
    if (isSaving || hasLockedAgreement) {
      return false
    }

    setIsSaving(true)

    try {
      const { data } = await axios.post(`/admin/rental_applications/${rentalApplication.id}`, {
        guarantor_bond_id: newBondId
      })

      const updatedRentalApplication = {
        ...rentalApplication,
        guarantor_bond_id: data.guarantor_bond_id
      }
      setRentalApplication(updatedRentalApplication)
      setGuarantorBondId(data.guarantor_bond_id)
      return true
    } catch (err: any) {
      window.alert(err.message)
      return false
    } finally {
      setIsSaving(false)
    }
  }

  const {
    agreements,
    created_at: createdAt,
    projected_occupancy_date: projectedOccupancyDate,
    status,
    status_metadata: statusMetadata,
    rent: applicationRent,
    listedRent,
    mls_listing
  } = rentalApplication

  const internalStatus = mls_listing?.internal_attributes?.internal_status
  const rent = applicationRent || listedRent

  const leaseAgreement = agreements
    .filter((agreement) => agreement.status !== AGREEMENT_STATUSES.CANCELED)
    .sort(
      (firstAgreement, secondAgreement) =>
        new Date(secondAgreement.created_at).getTime() -
        new Date(firstAgreement.created_at).getTime()
    )[0]
  const buildiumLeaseSync = useBuildiumLeaseSync(rentalApplication.id, leaseAgreement)
  const { buildiumLeaseId } = buildiumLeaseSync
  const hasNonCanceledAgreement = Boolean(leaseAgreement)
  const hasLockedAgreement = hasNonCanceledAgreement
  const canEditProgramType = hasCapability(admin, CAPABILITY_TYPES.LEASING_ADMIN)
  const isProgramTypeSelectDisabled = !canEditProgramType || isSaving || hasLockedAgreement
  const programTypeTooltipLabel = hasLockedAgreement
    ? 'Lease type cannot be changed after a lease has been created.'
    : 'You do not have permission to change the lease type. Ask a Leasing Admin to change it.'
  const showProgramTypeTooltip = hasLockedAgreement || !canEditProgramType
  const isKansasCityApplication = mls_listing?.region === REGIONS.KANSAS_CITY
  const isVanillaLeaseType = rentalProgramTypeAppliedFor === RENTAL_PROGRAM_TYPES.VANILLA
  const isLeaseVariantSelectDisabled =
    isSaving ||
    !isKansasCityApplication ||
    !isVanillaLeaseType ||
    !!guarantorBondId ||
    hasNonCanceledAgreement
  const leaseVariantTooltipLabel = hasNonCanceledAgreement
    ? 'Rental Purchase Program cannot be changed after a lease has been created.'
    : !isKansasCityApplication
    ? 'Rental Purchase Program is only available for Kansas City applications.'
    : !isVanillaLeaseType
    ? 'Rental Purchase Program requires a vanilla lease.'
    : guarantorBondId
    ? 'Rental Purchase Program does not support guarantor addendums.'
    : ''
  const showLeaseVariantTooltip = isLeaseVariantSelectDisabled && leaseVariantTooltipLabel
  const showBuildiumLeaseDetails =
    POST_LEASE_CREATION_STATUSES.has(status) ||
    (status === RENTAL_APPLICATION_STATUSES.CANCELED && !!buildiumLeaseId)
  const buildiumLeaseAlertState = getBuildiumLeaseAlertState({
    isPostLeaseCreation: POST_LEASE_CREATION_STATUSES.has(status),
    ...buildiumLeaseSync
  })
  const occupancyDate = get(
    leaseAgreement,
    'metadata.custom_fields.startDate',
    projectedOccupancyDate
  )

  const updateRentalProgramTypeAppliedFor = async (nextProgramType: string) => {
    if (isSaving || hasLockedAgreement || nextProgramType === rentalProgramTypeAppliedFor) {
      return
    }

    setIsSaving(true)

    try {
      const { data } = await axios.post(`/admin/rental_applications/${rentalApplication.id}`, {
        rental_program_type_applied_for: nextProgramType
      })

      setRentalApplication({
        ...rentalApplication,
        rental_program_type_applied_for: data.rental_program_type_applied_for
      })
      setRentalProgramTypeAppliedFor(data.rental_program_type_applied_for)
    } catch (err: any) {
      window.alert(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const updateLeaseVariant = async (nextLeaseVariant: string) => {
    if (isLeaseVariantSelectDisabled || nextLeaseVariant === leaseVariant) {
      return
    }

    setIsSaving(true)

    try {
      const { data } = await axios.post(`/admin/rental_applications/${rentalApplication.id}`, {
        lease_variant: nextLeaseVariant
      })

      setRentalApplication({
        ...rentalApplication,
        lease_variant: data.lease_variant
      })
      setLeaseVariant(data.lease_variant)
    } catch (err: any) {
      window.alert(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <ContentSectionCard padding collapsable={true} title={'Details'}>
        <Heading size='sm' as='h5' marginBottom={2}>
          Created: {moment(createdAt).format('MMM Do, h:mm A')}
        </Heading>
        <BuildiumLeaseSyncAlert state={buildiumLeaseAlertState} />

        <div className='flex flex-wrap gap-y-2'>
          <div style={{ flex: '1', minWidth: '50%' }}>
            <Text fontSize='xs'>Status</Text>
            <Text marginTop={0}>{startCase(status).replace('Upandup', 'Up&Up')}</Text>
            <Text fontSize='xs' marginTop={0}>
              {statusMetadata?.message}
            </Text>
          </div>
          <div style={{ flex: '1', minWidth: '50%' }}>
            <Text fontSize='xs'>Property Status</Text>
            <Text marginTop={0}>{startCase(internalStatus ?? '')}</Text>
          </div>
          {showBuildiumLeaseDetails && (
            <div style={{ flex: '1', minWidth: '50%' }}>
              <Text fontSize='xs'>Buildium Lease</Text>
              <BuildiumLeaseValue
                buildiumLeaseId={buildiumLeaseId}
                alertState={buildiumLeaseAlertState}
              />
            </div>
          )}

          <div style={{ flex: '1', minWidth: '50%' }}>
            <CrmField
              label='Priority Score'
              fieldType={FIELD_TYPES.VALUE}
              fieldValueType={FIELD_VALUE_TYPES.INTEGER}
              value={rentalApplication.priority_score}
              onChange={async (score) => {
                const result = await setPriority({
                  pk_columns: { id: rentalApplication.id },
                  priority_score: score
                })
                if (result.error) {
                  alert('Issue with setting priority, contact eng and refresh page')
                }
              }}
            />
          </div>
          <div style={{ flex: '1', minWidth: '50%' }}>
            <CrmField
              label='Received Initial Product Pitch'
              fieldType={FIELD_TYPES.VALUE}
              fieldValueType={FIELD_VALUE_TYPES.BOOLEAN}
              value={rentalApplication.received_initial_product_pitch_at !== null}
              onChange={async (recievedPitch) => {
                const result = await setReceivedInitialProductPitch({
                  pk_columns: { id: rentalApplication.id },
                  did_receive_initial_product_pitch: Boolean(recievedPitch)
                })
                if (result.error) {
                  alert('Issue with setting value, contact eng and refresh page')
                }
              }}
            />
            <Text marginTop={0} fontSize='xs'>
              Not Known is the same as False
            </Text>
          </div>

          <div style={{ flex: '1', minWidth: '50%' }}>
            <CrmField
              label='Application Rent'
              fieldType={FIELD_TYPES.VALUE}
              fieldValueType={FIELD_VALUE_TYPES.MONEY}
              value={rent}
              onChange={async (rent) => {
                const result = await setRent({
                  pk_columns: { id: rentalApplication.id },
                  rent: rent
                })
                if (result.error) {
                  alert('Issue with setting application rent, contact eng and refresh page')
                }
                setRentalApplication({
                  ...rentalApplication,
                  rent: result.data.update_rental_applications_by_pk.rent
                })
              }}
            />
          </div>
          <div style={{ flex: '1', minWidth: '50%' }}>
            <CrmField
              label='Projected Occupancy Date'
              fieldType={FIELD_TYPES.VALUE}
              fieldValueType={FIELD_VALUE_TYPES.DATE}
              value={projectedOccupancyDate || ''}
              onChange={updateProjectedOccupancyDate}
              disabled={hasLockedAgreement}
            />
          </div>
          <div style={{ flex: '1', minWidth: '50%' }}>
            <Text fontSize='xs'>Monthly Option Premium</Text>
            <Text marginTop={0}>${formatMoney(MONTHLY_OPTION_PREMIUM, 0)}</Text>
          </div>
          <div style={{ flex: '1', minWidth: '50%' }}>
            <Text fontSize='xs'>Total Monthly Payment</Text>
            <Text marginTop={0}>
              ${rent ? formatMoney(rent + MONTHLY_OPTION_PREMIUM, 0) : '--'}
            </Text>
          </div>

          <div style={{ flex: '1', minWidth: '50%' }}>
            <Text fontSize='xs'>Rent Ready Date</Text>
            <Text marginTop={0}>
              {error
                ? 'error'
                : loading
                ? 'loading'
                : data
                ? data.rent_ready_dates[0]?.earliest_move_in_date
                : null}
            </Text>
          </div>

          <div style={{ flex: 1 }}>
            <Text fontSize='xs'>Home Rematching Tool</Text>
            <Text marginTop={0}>
              <a
                target='_blank'
                rel='noopener noreferrer'
                href={`https://upandup.tryretool.com/apps/6899f83e-dc3d-11ec-b888-b74d10445f94/Leasing/Home%20Matching%20Tool?_releaseVersion=latest#${mls_listing.id}&mls_listing_id=${mls_listing.id}`}
              >
                {' '}
                Click here
              </a>
            </Text>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <Text fontSize='xs'>User Application</Text>
          <HStack>
            <Text
              marginTop={0}
            >{`https://app.upandup.co/application/${rentalApplication.id}`}</Text>
            <CopyIcon textToCopy={`https://app.upandup.co/application/${rentalApplication.id}`} />
          </HStack>
        </div>

        <div style={{ flex: 1 }}>
          <Text fontSize='xs'>Application Invite Link</Text>
          <HStack>
            <Text marginTop={0}>{`https://app.upandup.co/join/${rentalApplication.id}`}</Text>
            <CopyIcon textToCopy={`https://app.upandup.co/join/${rentalApplication.id}`} />
          </HStack>
        </div>
        <div style={{ flex: 1, marginTop: '20px' }}>
          <Text fontSize='xs'>Lease Type</Text>
          <Tooltip label={programTypeTooltipLabel} isDisabled={!showProgramTypeTooltip}>
            <span style={{ display: 'inline-block', maxWidth: '240px', width: '100%' }}>
              <Select
                size='sm'
                value={rentalProgramTypeAppliedFor || ''}
                onChange={(event) => updateRentalProgramTypeAppliedFor(event.target.value)}
                isDisabled={isProgramTypeSelectDisabled}
                pointerEvents={isProgramTypeSelectDisabled ? 'none' : 'auto'}
              >
                <option value={RENTAL_PROGRAM_TYPES.UPANDUP}>Up&Up Lease</option>
                <option value={RENTAL_PROGRAM_TYPES.VANILLA}>Vanilla Lease</option>
              </Select>
            </span>
          </Tooltip>
        </div>
        {canEditProgramType && (
          <div style={{ flex: 1, marginTop: '20px' }}>
            <Text fontSize='xs'>Rental Purchase Program (RPP/LPP)</Text>
            <Tooltip label={leaseVariantTooltipLabel} isDisabled={!showLeaseVariantTooltip}>
              <span style={{ display: 'inline-block', maxWidth: '280px', width: '100%' }}>
                <Select
                  size='sm'
                  value={leaseVariant}
                  onChange={(event) => updateLeaseVariant(event.target.value)}
                  isDisabled={isLeaseVariantSelectDisabled}
                  pointerEvents={isLeaseVariantSelectDisabled ? 'none' : 'auto'}
                >
                  <option value={LEASE_VARIANTS.STANDARD}>Standard rental</option>
                  <option value={LEASE_VARIANTS.KCPOA}>Rental Purchase Program (RPP/LPP)</option>
                </Select>
              </span>
            </Tooltip>
          </div>
        )}
        <div style={{ flex: 1, marginTop: '20px' }}>
          <Text fontSize='xs'>Guarantor Bond ID</Text>
          <HStack marginTop={0} spacing={2}>
            {guarantorBondId && <Text marginTop={0}>{guarantorBondId}</Text>}
            <Button
              size='xs'
              variant='outline'
              onClick={() => setIsGuarantorBondIdModalOpen(true)}
              isDisabled={hasLockedAgreement}
            >
              {guarantorBondId ? 'Edit' : 'Set'}
            </Button>
          </HStack>
        </div>
        <GuarantorBondIdModal
          isOpen={isGuarantorBondIdModalOpen}
          close={() => setIsGuarantorBondIdModalOpen(false)}
          currentBondId={guarantorBondId}
          save={updateGuarantorBondId}
          isSaving={isSaving}
        />
        <div>
          <Checkbox
            label={'Disable Daily Auto-Reminders (to submit documents)'}
            value={autoRemindersDisabledFlag}
            onChange={updateAutoRemindersDisabledFlag}
          ></Checkbox>
        </div>
      </ContentSectionCard>
      {occupancyDate && rent && (
        <div style={{ marginTop: '20px' }}>
          <PaymentSchedule
            occupancyDate={occupancyDate}
            hasGuarantorAddendum={!!guarantorBondId}
            leaseVariant={leaseVariant}
            rent={rent}
            rentalApplicationId={rentalApplication.id}
            rentalProgramTypeAppliedFor={rentalProgramTypeAppliedFor}
          />
        </div>
      )}
    </>
  )
}

export default ApplicationDetails
